/**
 * ADR-187 D1 — the facts of the contract a contract-addressed action acts on.
 *
 * With concurrent contracts the person's flat fields (contractStartDate,
 * contractEndDate, currentEpisodeContractNumber, currentEpisodeSource,
 * pendingRenewal) describe the PRIMARY (ADR-156 §2). A dialog acting on another
 * contract must never read them — it shows, checks and (for the fixed-term
 * sentence) saves that contract's own facts, from its contracts[] entry.
 *
 * Resolution order:
 *   1. the episode's contracts[] entry (ADR-156 §5);
 *   2. the flat person fields, ONLY when the acting contract is the primary, no
 *      contract is named, or the payload has no contracts[] (pre-ADR-156);
 *   3. the episode's own facts — never the primary's.
 *
 * @param {Object|null} employee  GET /employees/:id payload
 * @param {Object|null} episode   the acting contract (an episode, or any object with `id`)
 * @returns {{ episodeId: string|null, isPrimary: boolean, startDate: string|null,
 *   endDate: string|null, contractNumber: string|null, source: string|null,
 *   pendingRenewalOn: string|null }}
 */
export const contractActionFacts = (employee, episode = null) => {
  const contracts = Array.isArray(employee?.contracts) ? employee.contracts : [];
  const primaryId = employee?.primaryEmploymentEpisodeId || employee?.currentEmploymentEpisodeId || null;
  const episodeId = episode?.id || null;

  const entry = episodeId
    ? contracts.find((contract) => contract.id === episodeId) || null
    : contracts.find((contract) => contract.isPrimary) || null;

  if (entry) {
    return {
      episodeId: entry.id,
      isPrimary: Boolean(entry.isPrimary),
      startDate: entry.startDate || null,
      endDate: entry.termEndDate || null,
      contractNumber: entry.contractNumber || null,
      source: entry.source || null,
      pendingRenewalOn: entry.pendingRenewal?.effectiveOn || null
    };
  }

  const actsOnPrimary = !episodeId || contracts.length === 0 || (primaryId !== null && episodeId === primaryId);
  if (actsOnPrimary) {
    return {
      episodeId: episodeId || primaryId,
      isPrimary: true,
      startDate: employee?.contractStartDate || employee?.hireDate || null,
      endDate: employee?.contractEndDate || employee?.resignDate || null,
      contractNumber: employee?.currentEpisodeContractNumber || null,
      source: employee?.currentEpisodeSource || null,
      pendingRenewalOn: employee?.pendingRenewal?.effectiveOn || null
    };
  }

  // A named, non-primary contract the payload does not list: its own facts only.
  const ref = episode?.payload?.sourceReference || {};
  const number = ref.contractId;
  return {
    episodeId,
    isPrimary: false,
    startDate: episode?.startDate || null,
    endDate: null,
    contractNumber: number === undefined || number === null || number === '' ? null : String(number),
    source: ref.source || null,
    pendingRenewalOn: null
  };
};

const ymdOf = (value) => (value ? String(value).slice(0, 10) : null);
const dayBefore = (ymd) => {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const ddmmyyyy = (ymd) => ymd.split('-').reverse().join('/');

/**
 * ADR-188 D2 — the latest day a contract's start may be delayed to, mirroring
 * the server (hire.start-delayed):
 *  (a) before the contract's own scheduled end;
 *  (b) a secondary: before the primary's scheduled end;
 *  (c) the primary: not after any other open contract's start.
 * @param {Object|null} employee  GET /employees/:id payload (contracts[])
 * @param {Object} facts          contractActionFacts(employee, episode)
 * @returns {{ latestYmd: string|null, reason: string|null }} the tightest
 *   bound (inclusive) and the sentence explaining it; nulls when unbounded.
 */
export const startDelayLimit = (employee, facts) => {
  const contracts = Array.isArray(employee?.contracts) ? employee.contracts : [];
  const limits = [];

  const ownEnd = ymdOf(facts?.endDate);
  if (ownEnd) {
    limits.push({ latestYmd: dayBefore(ownEnd), reason: `The delayed start must stay before this contract's end date (${ddmmyyyy(ownEnd)})` });
  }
  if (facts && !facts.isPrimary) {
    const primary = contracts.find((c) => c.isPrimary && !c.endDate);
    const primaryEnd = ymdOf(primary?.termEndDate);
    if (primaryEnd) {
      limits.push({ latestYmd: dayBefore(primaryEnd), reason: `The delayed start must stay before the primary contract ends (${ddmmyyyy(primaryEnd)})` });
    }
  } else if (facts) {
    const otherStarts = contracts
      .filter((c) => !c.endDate && c.id !== facts.episodeId && c.startDate)
      .map((c) => ymdOf(c.startDate))
      .sort();
    if (otherStarts.length > 0) {
      limits.push({
        latestYmd: otherStarts[0],
        reason: `The primary contract cannot start after another of this person's contracts (from ${ddmmyyyy(otherStarts[0])}) — make that contract primary first, or delay it too`
      });
    }
  }

  if (limits.length === 0) {
    return { latestYmd: null, reason: null };
  }
  return limits.reduce((tightest, limit) => (limit.latestYmd < tightest.latestYmd ? limit : tightest));
};

/**
 * ADR-188 D2d — the earliest day a start may be delayed to: a delay moves the
 * start LATER (the day after the current start at the soonest) and into the
 * future (tomorrow at the soonest). Mirrors hire.start-delayed (NOT_A_DELAY).
 * @param {Object} facts      contractActionFacts(employee, episode)
 * @param {string} todayYmd   YYYY-MM-DD
 * @returns {{ earliestYmd: string, reason: string }}
 */
export const startDelayEarliest = (facts, todayYmd) => {
  const next = (ymd) => {
    const d = new Date(`${ymd}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  };
  const tomorrow = next(todayYmd);
  const start = ymdOf(facts?.startDate);
  const afterStart = start ? next(start) : null;
  if (afterStart && afterStart > tomorrow) {
    return {
      earliestYmd: afterStart,
      reason: `A delay moves the start later — it must be after the current start (${ddmmyyyy(start)})`
    };
  }
  return { earliestYmd: tomorrow, reason: 'The new start must be in the future' };
};
